import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import type { OcdDetailsQuestionTemplate, OcdDetailsQuestionsData } from './settingsTypes';

export class OcdDetailsQuestionsService {
  private static instance: OcdDetailsQuestionsService;
  private readonly COLLECTION_PRIVATE = 'metadata_private';
  private readonly DOC_ID = 'ocd_details_questions';

  private constructor() {}

  public static getInstance(): OcdDetailsQuestionsService {
    if (!OcdDetailsQuestionsService.instance) {
      OcdDetailsQuestionsService.instance = new OcdDetailsQuestionsService();
    }
    return OcdDetailsQuestionsService.instance;
  }

  public async getQuestionTemplates(): Promise<OcdDetailsQuestionTemplate[]> {
    const docRef = doc(db, this.COLLECTION_PRIVATE, this.DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as OcdDetailsQuestionsData;
      return data.question_templates || [];
    }

    return [];
  }

  public async updateQuestionTemplates(templates: OcdDetailsQuestionTemplate[]): Promise<void> {
    const privateDocRef = doc(db, this.COLLECTION_PRIVATE, this.DOC_ID);
    await setDoc(privateDocRef, { question_templates: templates, updatedAt: serverTimestamp() }, { merge: true });
  }

  public async addQuestionTemplate(template: OcdDetailsQuestionTemplate): Promise<void> {
    const templates = await this.getQuestionTemplates();

    // Check if template with this ocdProfileKey already exists
    if (templates.some((t) => t.ocdProfileKey === template.ocdProfileKey)) {
      throw new Error(`Question template with ocdProfileKey "${template.ocdProfileKey}" already exists`);
    }

    templates.push(template);
    await this.updateQuestionTemplates(templates);
  }

  public async updateQuestionTemplate(originalOcdProfileKey: string, updates: Partial<OcdDetailsQuestionTemplate>): Promise<void> {
    const templates = await this.getQuestionTemplates();
    const index = templates.findIndex((t) => t.ocdProfileKey === originalOcdProfileKey);

    if (index === -1) {
      throw new Error(`Question template with ocdProfileKey "${originalOcdProfileKey}" not found`);
    }

    // If ocdProfileKey is being changed, check if the new key already exists
    if (updates.ocdProfileKey && updates.ocdProfileKey !== originalOcdProfileKey) {
      if (templates.some((t) => t.ocdProfileKey === updates.ocdProfileKey)) {
        throw new Error(`Question template with ocdProfileKey "${updates.ocdProfileKey}" already exists`);
      }
    }

    templates[index] = { ...templates[index], ...updates };
    await this.updateQuestionTemplates(templates);
  }

  public async deleteQuestionTemplate(ocdProfileKey: string): Promise<void> {
    const templates = await this.getQuestionTemplates();
    const filteredTemplates = templates.filter((t) => t.ocdProfileKey !== ocdProfileKey);

    if (filteredTemplates.length === templates.length) {
      throw new Error(`Question template with ocdProfileKey "${ocdProfileKey}" not found`);
    }

    await this.updateQuestionTemplates(filteredTemplates);
  }

  public async reorderQuestionTemplates(templates: OcdDetailsQuestionTemplate[]): Promise<void> {
    // Update order property for each template
    const reorderedTemplates = templates.map((template, index) => ({
      ...template,
      order: index,
    }));

    await this.updateQuestionTemplates(reorderedTemplates);
  }
}

export const ocdDetailsQuestionsService = OcdDetailsQuestionsService.getInstance();
